import React, {
  ComponentProps,
  useCallback,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  useEffect,
  ComponentType,
} from 'react';

import Editor from '@draft-js-plugins/editor';
import createMentionPlugin from '@draft-js-plugins/mention';
import type { Placement } from '@popperjs/core';
import {
  EditorState,
  getDefaultKeyBinding,
  EditorProps,
  convertFromRaw,
  RawDraftContentState,
} from 'draft-js';
import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { useLocale } from '@adsk/alloy-react-locale';
import { MenuCategory } from '@adsk/alloy-react-menu';
import Overlay from '@adsk/alloy-react-overlay';
import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import { LoadingSuggestion } from './LoadingSuggestion';
import MentionCallout from './MentionCallout';
import MentionPopover, { MentionPopoverProps } from './MentionPopover';
import MentionSuggestion from './MentionSuggestion';
import {
  defaultGetMentionName,
  defaultRegExp,
  MentionData,
  MentionPropType,
  MENTION_ANALYTICS,
  MENTION_TYPES,
} from '../consts';
import { canShowCategory } from '../utils/canShowCategory';
import { convertToRawWithMentions } from '../utils/convertToRawWithMentions';
import draftJsCss from '../utils/draftJsCss';

function isFirst(suggestions: MentionData[], mention: MentionData) {
  const [first] = suggestions;
  return mention.id === first.id;
}

export type MentionPluginProps = Pick<
  StylableComponent<HTMLDivElement>,
  'style' | 'className'
> &
  Pick<
    EditorProps,
    | 'editorKey'
    | 'placeholder'
    | 'spellCheck'
    | 'onFocus'
    | 'onBlur'
    | 'readOnly'
  > & {
    autoFocus?: boolean;
    isLoading?: boolean;
    placement?: Placement;
    editorState?: EditorState;
    suggestions: MentionData[];
    currentUserId?: string | null;
    onChange?: (changedText: string, editorState: EditorState) => void;
    onSearchChange: (event: { trigger: string; value: string }) => void;
    onAddMention?(Mention: MentionData): void;
    mentionFormatter?: (mention: MentionData) => string;
    getMentionName?: (mention: MentionData) => string;
    getMentionType?: (mention: MentionData) => string;
    getMentionId?: (mention: MentionData) => string;
    renderPopover?: ComponentType<MentionPopoverProps>;
    renderSuggestion?: (
      props: ComponentProps<typeof MentionSuggestion>,
    ) => JSX.Element;
  };

const MentionPlugin = ({
  className,
  style,
  editorKey,
  placeholder,
  readOnly,
  spellCheck,
  autoFocus,
  currentUserId,
  placement = Overlay.PLACEMENTS.BOTTOM_START,
  editorState: editorStateProp,
  suggestions,
  isLoading,
  onChange: onChangeProp,
  onAddMention,
  onSearchChange,
  onFocus,
  onBlur,
  getMentionName = defaultGetMentionName,
  getMentionType = (mention) => mention.type,
  getMentionId = (mention) => mention.id?.toString() || '',
  mentionFormatter = (mention) =>
    `@{"type": "${getMentionType(mention)}", "id": "${getMentionId(
      mention,
    )}", "name": "${getMentionName(mention)}"}`,
  // eslint-disable-next-line @adsk/react-render-props/component-to-render-prop
  renderPopover = MentionPopover,
  renderSuggestion = (props) => <MentionSuggestion {...props} />,
}: MentionPluginProps) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const editorRef = useRef<Editor>(null);
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createEmpty(),
  );
  const [open, setOpen] = useState(false);

  const { MentionSuggestions, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      supportWhitespace: true,
      entityMutability: 'IMMUTABLE',
      mentionComponent: ({ mention, ...props }) => (
        <MentionCallout mention={mention as MentionData} {...props} />
      ),
      popperOptions: {
        placement,
      },
      theme: {
        mentionSuggestionsPopup: 'mention-suggestions-popup',
      },
      mentionRegExp: defaultRegExp,
    });
    return {
      plugins: [mentionPlugin],
      MentionSuggestions: mentionPlugin.MentionSuggestions,
    };
  }, [placement]);

  const onOpenChange = useCallback((_open: boolean) => {
    setOpen(_open);

    if (_open) {
      analytics.track(MENTION_ANALYTICS.CREATE_START);
    }
  }, []);

  const onChange = useCallback(
    (nextEditorState: EditorState) => {
      onChangeProp?.(
        convertToRawWithMentions(nextEditorState, mentionFormatter),
        nextEditorState,
      );

      setEditorState(nextEditorState);
    },
    [mentionFormatter, onChangeProp],
  );

  useEffect(() => {
    if (autoFocus) {
      setImmediate(() => {
        editorRef?.current?.focus();
      });
    }
  }, [autoFocus]);

  return (
    <div
      className={className}
      css={[draftJsCss, style]}
      onClick={() => editorRef.current?.focus()}
    >
      <Editor
        ref={editorRef}
        editorKey={editorKey}
        editorState={editorStateProp || editorState}
        onChange={onChange}
        plugins={plugins}
        onFocus={onFocus}
        placeholder={placeholder}
        spellCheck={spellCheck}
        readOnly={readOnly}
        onBlur={onBlur}
        keyBindingFn={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          // Preserve default behavior of Mention plugin key bindings
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            return undefined;
          }

          if (e.key === 'Escape' && open) {
            analytics.track(MENTION_ANALYTICS.CREATE_END);
            return undefined;
          }

          return getDefaultKeyBinding(e);
        }}
      />
      <MentionSuggestions
        open={open}
        onOpenChange={onOpenChange}
        suggestions={suggestions}
        onSearchChange={onSearchChange}
        onAddMention={(mention) => {
          onAddMention?.(mention as MentionData);

          analytics.track(MENTION_ANALYTICS.CREATE_SELECT, {
            mention_type: mention.type,
            mention_name: mention.name,
            mention_current_user: mention.id === currentUserId,
          });
        }}
        popoverContainer={renderPopover}
        entryComponent={({
          mention,
          isFocused,
          searchValue,
          onMouseDown,
          onMouseUp,
        }) => (
          <>
            {isLoading && isFirst(suggestions, mention as MentionData) && (
              <LoadingSuggestion searchValue={searchValue} />
            )}
            {canShowCategory(suggestions, mention) && (
              <MenuCategory
                title={T.translate(mention.type.toUpperCase())}
                style={{
                  h4: {
                    background: theme.colors.charcoal050,
                    height: 32,
                    width: '100%',
                    padding: '8px 16px',
                    margin: 0,
                  },
                }}
              />
            )}
            {renderSuggestion({
              mention: mention as MentionData,
              isFocused,
              searchValue,
              onMouseDown,
              onMouseUp,
            })}
          </>
        )}
      />
    </div>
  );
};

MentionPlugin.displayName = 'MentionPlugin';

MentionPlugin.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** editor key is unique identifier used when multiple editors used on the same page */
  editorKey: PropTypes.string,
  /** placeholder text displayed when editor is empty */
  placeholder: PropTypes.string,
  /** user id we report in analytics if provided */
  currentUserId: PropTypes.string,
  /** enables editor check spelling (using browser's implementation). */
  spellCheck: PropTypes.bool,
  /** is editor read only, when enabled editor editing is disabled */
  readOnly: PropTypes.bool,
  /** Triggers on editor focus */
  onFocus: PropTypes.func,
  /** Triggers on editor blur */
  onBlur: PropTypes.func,
  /** mention suggestions menu position according to Overlay placements */
  placement: PropTypes.oneOf(Object.values(Overlay.PLACEMENTS)),
  /** Immutable Record that represents the entire state of a Draft editor
     Used for controlled editor options, can be reset using EMPTY_STATE
     https://draftjs.org/docs/api-reference-editor-state
  */
  editorState: PropTypes.object,
  /** list of suggestions to display when triggering a mention */
  suggestions: PropTypes.arrayOf(MentionPropType),
  /** list of suggestions to display when triggering a mention */
  decorators: PropTypes.arrayOf(
    PropTypes.shape({
      strategy: PropTypes.func,
      component: PropTypes.func,
    }),
  ),
  /** display loading while searching, used when data isn't fully loaded for better UX */
  isLoading: PropTypes.bool,
  /** Formatter function that generates the mention text according
     to the mention and mention selector functions above.      */
  mentionFormatter: PropTypes.func,
  /** Selector function to get the name when generating the mention text. */
  getMentionName: PropTypes.func,
  /** Selector function to get the type when generating the mention text. */
  getMentionType: PropTypes.func,
  /** Selector function to get the id when generating the mention text. */
  getMentionId: PropTypes.func,
  /** renders suggestion popover component */
  renderPopover: PropTypes.func,
  /** renders suggestion component */
  renderSuggestion: PropTypes.func,
  /** triggered when editor value is changed
     used for controlled editor together with editorState prop */
  onChange: PropTypes.func,
  /** triggered when mention is trigger typed (by default using @)
     with value from editor used for searching suggestions  */
  onSearchChange: PropTypes.func.isRequired,
  /** triggered when new mention added to editor */
  onAddMention: PropTypes.func,
};

const editorStateFromRaw = (raw: RawDraftContentState) =>
  EditorState.createWithContent(convertFromRaw(raw));

export default Object.assign(MentionPlugin, {
  PLACEMENTS: Overlay.PLACEMENTS,
  EMPTY_STATE: EditorState.createEmpty(),
  editorStateFromRaw,
  TYPES: MENTION_TYPES,
});
